import React from "react";
import PropTypes from "prop-types";

import Controls from "./controls";
import styles from "./style.module.scss";
import imgCircle from "./img/circle.svg";
import { injectIntl, FormattedMessage } from "react-intl";

/**
 * BlottingPaper Component.
 * This component renders the blotting paper section with its items.
 * It can be used in different sections like PREDICTIONS or RESULTS,
 * and provides visual feedback for items based on their volume.
 *
 * @prop {string} section - Represents the current section. E.g., PREDICTIONS, RESULTS, etc.
 * @prop {string} heading - Represents the heading/title for the section.
 * @prop {string} [description] - Represents a description or additional info for the section.
 * @prop {bool} [showCorrection] - A flag to indicate if correction messages should be shown.
 * @prop {Array} [data] - Represents data for individual items in the section.
 *
 */
class BlottingPaperBase extends React.Component {
  render() {
    const { intl } = this.props;
    return (
      <div className={styles.section} dir="auto">
        <h5
          style={
            this.props.section === "RESULTS" ? { margin: "32px 0px -16px" } : {}
          }
        >
          <FormattedMessage
            id={this.props.heading}
            defaultMessage={this.props.heading}
            description=""
          />
        </h5>

        {this.props.description && (
          <p>
            <FormattedMessage
              id={this.props.description}
              defaultMessage={this.props.description}
              description=""
            />
          </p>
        )}

        <ul className={styles.ul}>
          {this.props.data.map((item, index) => (
            <li
              className={styles.li}
              style={{
                width:
                  this.props.section === "PREDICTIONS"
                    ? "calc(20% - 32px)"
                    : "calc(25% - 32px)",
              }}
              key={item.label}
            >
              {/* Conditionally show volume for predictions */}
              {this.props.section === "PREDICTIONS" && (
                <p
                  className={styles.zoneVolume}
                  aria-label={intl.formatMessage({
                    id: this.props.component_strings.demo_aria_label,
                    description: "",
                    defaultMessage:
                      this.props.component_strings.demo_aria_label,
                  })}
                >
                  {item.desired_volume + "μl"}
                </p>
              )}

              <div className={styles.indicator}>
                {/* Show controls only for predictions and if controls are enabled */}
                {this.props.section === "PREDICTIONS" &&
                  item.controls === true && (
                    <Controls
                      index={index}
                      intl={intl}
                      component_strings={this.props.component_strings}
                    />
                  )}

                <img
                  src={imgCircle}
                  className={styles.img}
                  alt={intl.formatMessage(
                    {
                      id: this.props.component_strings.img_alt,
                      defaultMessage: this.props.component_strings.img_alt,
                      description: "",
                    },
                    {
                      label: item.label,
                      percentage_area: (item.volume / 50) * 100,
                    }
                  )}
                  style={{
                    pointerEvents: "none",
                    width: (item.volume / 50) * 100 + "%",
                  }}
                />
              </div>

              {/* Show label and conditionally show volume for results */}
              <p className={styles.zoneLabel}>
                {item.label}
                {this.props.section === "RESULTS" && " | " + item.volume + "μl"}
              </p>

              {/* Show correction if flag is enabled */}
              <div className={styles.correction}>
                {this.props.showCorrection &&
                  item.volume === this.props.ideal[index].volume && (
                    <p className={styles.correct}>
                      <FormattedMessage
                        id={this.props.component_strings.correct_string}
                        defaultMessage={
                          this.props.component_strings.correct_string
                        }
                        description=""
                      />
                    </p>
                  )}
                {this.props.showCorrection &&
                  !(item.volume === this.props.ideal[index].volume) && (
                    <p className={styles.incorrect}>
                      <FormattedMessage
                        id={this.props.component_strings.incorrect_string}
                        defaultMessage={
                          this.props.component_strings.incorrect_string
                        }
                        description=""
                      />
                    </p>
                  )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }
}

// Prop types for the BlottingPaper component
BlottingPaperBase.propTypes = {
  section: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  description: PropTypes.string,
  showCorrection: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      desired_volume: PropTypes.number,
      controls: PropTypes.bool,
      volume: PropTypes.number,
    })
  ).isRequired,
};

// Default props for the BlottingPaper component
BlottingPaperBase.defaultProps = {
  showCorrection: false,
};

export const BlottingPaper = injectIntl(BlottingPaperBase);
